// @ts-strict-ignore
import * as React from 'react';
import CommonNotificationSettingsModal from '../sharedComponents/CommonNotificationSettingsModal';
import {
    tryUpdateAppSettings,
    tryUpdateAppSettingsV2,
    UpdateNotificationSettingsRequest,
    UpdateStepNotificationConfigRequest,
} from '../../actions/appSettings';
import { useDispatch, useSelector } from 'react-redux';
import { AppSettingsUpdatable, AppSettingsV2Updatable } from '../../actions/updatable';
import { RootState } from '../../reducers/reducers';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectCommonNotificationFromAppNotificationId,
    selectDestinationInfoByDestinationStepConfigId,
    selectStepConfigDataFromStepGraphs,
    selectWorkflowId,
} from '../../selectors/selectors';
import { AppComponentProps } from '../../typescriptHelpers';
import { AppNotificationType, StepNotificationConfigOverride } from '../../types';
import { dayjs } from '@/extendedDayjs';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { Flex } from 'antd/lib';
import Tag from 'antd/lib/tag';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { InlineStepConfigDescription } from '../common/InlineStepConfigDescription';
import { Destination } from '@/types/destinations';

const useScopeDestinations = (appId: string, stepConfigId?: string) => {
    const stepGraphStepsMap = useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, appId));
    const destinationInfoById = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const stepGraphStep = stepConfigId ? stepGraphStepsMap?.[stepConfigId] : undefined;
    const destinations = React.useMemo<Destination[]>(
        () =>
            stepGraphStep ?
                stepGraphStep.destinationStepConfigIds
                    .map((id) => destinationInfoById[id])
                    .filter((destination): destination is Destination => destination !== undefined)
            :   Object.values(destinationInfoById),
        [stepGraphStep, destinationInfoById],
    );
    return { stepGraphStep, destinations };
};

const NotificationScopeRow: React.FC<{ appId: string; stepConfigId?: string }> = ({ appId, stepConfigId }) => {
    const { stepGraphStep, destinations } = useScopeDestinations(appId, stepConfigId);
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    if (!stepGraphStep) {
        return null;
    }

    return (
        <Flex
            align="center"
            gap={8}
            style={{
                border: `1px solid ${antGrey.grey5}`,
                borderRadius: 4,
                padding: '12px 16px',
                marginBottom: 20,
            }}
        >
            <Tag
                style={{
                    ...tagStyle,
                    color: runwayColors.primary,
                    borderColor: runwayColors.primary,
                    backgroundColor: 'transparent',
                }}
            >
                Override
            </Tag>
            <DestinationCounter size={20} destinations={destinations} />
            <InlineStepConfigDescription
                stepGraphStep={stepGraphStep}
                platform={platform}
                appStoreType={appStoreType}
                isWorkflowsCIStepEnabled
            />
        </Flex>
    );
};

interface NotificationSettingsModalProps extends AppComponentProps {
    notificationId: AppNotificationType;
    onClose: () => void;
    onAfterClose: () => void;
    open: boolean;

    // Step override props — when provided, the modal creates/updates a step override instead of app-level settings
    stepConfigId?: string;
    stepConfigName?: string;
    workflowId?: string;
    override?: StepNotificationConfigOverride | null;
}

const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = (props: NotificationSettingsModalProps) => {
    const dispatch = useDispatch();
    const isStepOverride = props.stepConfigId != null;
    const isCreatingOverride = isStepOverride && props.override == null;

    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, props.appId));

    const onSubmit = (values: any) => {
        const hourOfDay = values.hourOfDay ? dayjs(values.hourOfDay, 'HH:mm').clone().utc().hour() : null;

        if (isStepOverride) {
            const request: UpdateStepNotificationConfigRequest = {
                orgId: props.orgId,
                appId: props.appId,
                workflowId: props.workflowId ?? workflowId,
                stepConfigId: props.stepConfigId,
                notificationId: props.notificationId,
                enabled: values.enabled,
                settings: {
                    ...values,
                    hourOfDayForNotification: hourOfDay,
                },
                channels: values.channels ?? [],
            };
            dispatch(
                tryUpdateAppSettingsV2(
                    AppSettingsV2Updatable.UpdateStepNotificationConfig,
                    request,
                    props.notificationId,
                ),
            );
        } else {
            const request: UpdateNotificationSettingsRequest = {
                orgId: props.orgId,
                appId: props.appId,
                notificationId: props.notificationId,
                hourOfDayForNotification: hourOfDay,
                appleProfileAndCertificateReminderDaysBeforeExpiration:
                    values.appleProfileAndCertificateReminderDaysBeforeExpiration,
                bundleSizeIncreaseConditions: values.bundleSizeIncreaseConditions,
                bundleSizeCellularDownloadLimitThresholdMB: values.bundleSizeCellularDownloadLimitThresholdMB,
                bundleSizeAlertNotifyFor: values.bundleSizeAlertNotifyFor,
                ...values,
            };
            dispatch(
                tryUpdateAppSettings(AppSettingsUpdatable.UpdateNotificationSetting, request, props.notificationId),
            );
        }
    };

    const appNotification = useSelector((state: RootState) =>
        selectCommonNotificationFromAppNotificationId(state, props.appId, props.notificationId),
    );

    // For step overrides with existing settings, merge override settings on top of app-level
    const notification = React.useMemo(() => {
        if (!isStepOverride || !props.override) return appNotification;
        return {
            ...appNotification,
            ...(props.override.settings ?? {}),
            enabled: props.override.enabled,
            ...(props.override.channels?.length > 0 ? { channels: props.override.channels } : {}),
        };
    }, [appNotification, isStepOverride, props.override]);

    const actionType =
        isStepOverride ?
            AppSettingsV2Updatable.UpdateStepNotificationConfig
        :   AppSettingsUpdatable.UpdateNotificationSetting;

    const actionButtonText =
        isCreatingOverride ? `Create override for ${props.stepConfigName}`
        : isStepOverride ? `Update override for ${props.stepConfigName}`
        : undefined;

    return (
        <CommonNotificationSettingsModal
            onSubmit={onSubmit}
            actionType={actionType}
            notification={notification}
            actionButtonText={actionButtonText}
            permissionedActionType={RunwayUserAction.UAUpdateAppSettings}
            scopeDescription={<NotificationScopeRow appId={props.appId} stepConfigId={props.stepConfigId} />}
            {...props}
        />
    );
};

export default NotificationSettingsModal;
